/**
 * Password reset transport — the two REST calls the migration reset flow makes
 * (E01-F03, web side of T02/T03/T04).
 *
 * NEW in v2: the legacy app had no reset flow. These are the contract paths the
 * Fastify `/auth` router (E01-F03 backend) honours:
 *
 *   POST /auth/request-reset  { email }               → { success, message, reset_token? }
 *   POST /auth/reset-password { token, new_password }  → { success, message }
 *
 * Both go through the typed `apiClient`, so the response is normalized to
 * `ApiResult<T>` and NEVER throws. The shapes are validated with the @eer/shared
 * schemas at the boundary so a malformed response cannot reach the UI as a
 * half-typed value. Web never imports `zod` directly — every schema comes
 * through @eer/shared (the single source of truth).
 */
import {
  requestResetResponseSchema,
  resetPasswordResponseSchema,
  type ApiResult,
  type RequestResetResponse,
  type ResetPasswordResponse,
} from '@eer/shared';
import { apiClient } from '../api/client.js';

/**
 * Request a password reset for `email` (`POST /auth/request-reset`). The server
 * answers identically for a known and an unknown email (anti-enumeration), so a
 * success here NEVER confirms the email exists. In non-production the response
 * carries `reset_token` (no email provider wired) — used by the e2e flow.
 */
export async function requestPasswordReset(
  email: string,
): Promise<ApiResult<RequestResetResponse>> {
  const res = await apiClient.post<unknown>(
    '/auth/request-reset',
    { email },
    { command: 'request_reset' },
  );
  if (!res.ok) return res;
  const parsed = requestResetResponseSchema.safeParse(res.data);
  if (!parsed.success) {
    return {
      ok: false,
      error: {
        code: 'INTERNAL',
        message: 'The server returned an invalid response to the reset request.',
      },
    };
  }
  return { ok: true, data: parsed.data };
}

/**
 * Redeem a reset token and set a new password (`POST /auth/reset-password`).
 * A `CONFLICT` error means the token is unknown/expired/already used — the view
 * surfaces it as "ссылка недействительна" without changing any state.
 */
export async function submitPasswordReset(
  token: string,
  newPassword: string,
): Promise<ApiResult<ResetPasswordResponse>> {
  const res = await apiClient.post<unknown>(
    '/auth/reset-password',
    { token, new_password: newPassword },
    { command: 'reset_password' },
  );
  if (!res.ok) return res;
  const parsed = resetPasswordResponseSchema.safeParse(res.data);
  if (!parsed.success) {
    return {
      ok: false,
      error: {
        code: 'INTERNAL',
        message: 'The server returned an invalid response while resetting the password.',
      },
    };
  }
  return { ok: true, data: parsed.data };
}
